/**
 * ExistingContractorModal — F11 T11.2b.
 *
 * Wyswietla sie po wykryciu duplikatu NIP (auto-check w `ContractorDetail` lub 409 NIP_TAKEN
 * z POST /api/contractors). UX zaprojektowane na callu z zarzadem 2026-04-30 (ADR-0019):
 * - hasAccess=false  -> przycisk "Wlacz widocznosc" -> POST /:id/company-access -> redirect do /:id
 * - hasAccess=true   -> przycisk "Otworz karte"     -> redirect do /:id
 *
 * `companyId` (required prop): konkretna firma, KTOREJ udostepniamy widocznosc kontrahenta
 * (body POST /company-access). Callerzy MUSZA dostarczyc realne UUID — NIGDY sentinel
 * "Wszystkie firmy" (`ALL_COMPANIES`), ktory backend odrzuci (walidacja UUID w zod schemacie).
 * Komponent celowo NIE czyta `getStoredActiveCompanyId()` sam — w trybie "Wszystkie firmy"
 * ta wartosc bylaby sentinelem, nie realna firma (patrz `lib/active-company.ts`).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { useToast } from '@/contexts/ToastContext';
import { useWithMenuItem } from '@/hooks/useWithMenuItem';
import { api } from '@/lib/api';
import { extractErrorCode } from '@/lib/errors';

export interface ExistingContractorMatch {
  contractorId: string;
  name: string;
  homeCompanyName: string;
  hasAccessFromCurrentCompany: boolean;
}

export interface ExistingContractorModalProps {
  open: boolean;
  match: ExistingContractorMatch | null;
  onClose: () => void;
  /** Konkretna firma (UUID) dla body POST /company-access — nigdy sentinel "Wszystkie". */
  companyId: string;
}

export function ExistingContractorModal({
  open,
  match,
  onClose,
  companyId,
}: ExistingContractorModalProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  // Przejście z karty „nowy" na kartę istniejącego kontrahenta niesie kontekst listy — patrz
  // `useWithMenuItem`. Dotyczy TYLKO obrębu listy kontrahentów (karta `/contractors/new`): w oknie
  // osadzonym na formularzu dokumentu (np. faktury) bieżący adres należy do innej listy, więc
  // reguła rodziny listy w hooku zwraca ścieżkę bez kontekstu.
  const withMenuItem = useWithMenuItem();
  const [submitting, setSubmitting] = useState(false);

  if (!match) return null;

  const hasAccess = match.hasAccessFromCurrentCompany;

  async function handleGrant() {
    if (!match) return;
    setSubmitting(true);
    try {
      await api.post(`/contractors/${match.contractorId}/company-access`, {
        companyId,
      });
      toast.show({ variant: 'success', title: t('tenant.contractors.toast.shared') });
      navigate(withMenuItem(`/contractors/${match.contractorId}`));
    } catch (err) {
      const code = extractErrorCode(err);
      if (code === 'ALREADY_GRANTED') {
        // Wyscig — ktos juz wczesniej dorzucil. Otworz karte.
        navigate(withMenuItem(`/contractors/${match.contractorId}`));
        return;
      }
      toast.show({ variant: 'error', title: t('tenant.contractors.toast.shareError') });
    } finally {
      setSubmitting(false);
    }
  }

  function handleOpen() {
    if (!match) return;
    navigate(withMenuItem(`/contractors/${match.contractorId}`));
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t(
        hasAccess
          ? 'tenant.contractors.modalExists.titleHasAccess'
          : 'tenant.contractors.modalExists.titleNoAccess',
      )}
      size="md"
      role="alertdialog"
      footer={
        <div className="flex justify-end gap-2" data-testid="existing-contractor-modal-footer">
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            {t('tenant.contractors.modalExists.cancel')}
          </Button>
          {hasAccess ? (
            <Button onClick={handleOpen} data-testid="existing-contractor-modal-open-btn">
              {t('tenant.contractors.modalExists.open')}
            </Button>
          ) : (
            <Button
              onClick={handleGrant}
              disabled={submitting}
              data-testid="existing-contractor-modal-grant-btn"
            >
              {t('tenant.contractors.modalExists.grant')}
            </Button>
          )}
        </div>
      }
    >
      <p className="text-sm text-text-secondary" data-testid="existing-contractor-modal-body">
        {t(
          hasAccess
            ? 'tenant.contractors.modalExists.bodyHasAccess'
            : 'tenant.contractors.modalExists.bodyNoAccess',
          { name: match.name, company: match.homeCompanyName },
        )}
      </p>
    </Modal>
  );
}
